/**
 * Renders admin-defined custom fields (crm_custom_fields) as form inputs.
 *
 *   <DynamicFields fields={defs} values={form.custom_values}
 *                  onChange={(v) => ...} errors={errors} />
 *
 * `errors` is the flat map from validationErrors(); custom field errors
 * arrive as `custom_values.<key>`.
 */
export default function DynamicFields({ fields, values = {}, onChange, errors = {} }) {
  if (!fields.length) return null;

  const set = (key, value) => onChange({ ...values, [key]: value });

  return fields.map((f) => {
    const value = values[f.key] ?? '';
    const err = errors[`custom_values.${f.key}`];
    const label = f.label + (f.is_required ? ' *' : '');
    let input;

    switch (f.type) {
      case 'textarea':
        input = <textarea rows={3} value={value} onChange={(e) => set(f.key, e.target.value)} />;
        break;
      case 'number':
        input = <input type="number" value={value} onChange={(e) => set(f.key, e.target.value)} />;
        break;
      case 'date':
        input = <input type="date" value={value} onChange={(e) => set(f.key, e.target.value)} />;
        break;
      case 'select':
        input = (
          <select value={value} onChange={(e) => set(f.key, e.target.value)}>
            <option value="">Select…</option>
            {(f.options || []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        );
        break;
      case 'checkbox':
        input = (
          <label className="check">
            <input
              type="checkbox"
              checked={!!values[f.key]}
              onChange={(e) => set(f.key, e.target.checked)}
            />
            Yes
          </label>
        );
        break;
      default:
        input = <input type="text" value={value} onChange={(e) => set(f.key, e.target.value)} />;
    }

    return (
      <div className="field" key={f.id}>
        <label>{label}</label>
        {input}
        {err && <div className="err">{err}</div>}
      </div>
    );
  });
}

/** Display helper for a stored custom value. */
export function formatCustomValue(field, value) {
  if (value === undefined || value === null || value === '') return '—';
  if (field.type === 'checkbox') return value ? 'Yes' : 'No';
  return String(value);
}
